// cleanup_convert_to_typescript_file_GENERICS

/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { template } from 'lodash';

import useLocale from 'components/useLocale';
import { useAppConfig } from 'components/appConfig/AppConfig';
import { isWidgetLocked } from 'components/workspace/utils';
import ListPill from 'components/listPill/ListPill';
import UserPreferencesContext from 'components/withUserPreferences/UserPreferencesContext';
import { fetchAllWidgets, fetchWorkspaceDefaults } from 'api/workspace/service';
import { getConfigSettings } from 'api/config/service';
import uniqId from 'utils/uniqId';
import { unsubscribeComponent } from 'utils/unsubscribeComponent';

import useStyles from './addWidget.styles';
import { withinMaxWidgetLimit, WIDGET_REDUCER_ACTIONS } from './AddWidget.constants';

const { UPDATE_MAX_WIDGETS, UPDATE_WIDGET_LIST } = WIDGET_REDUCER_ACTIONS;
const subscriptions = { getConfigSettings: null };
const initialState = {
  allWidgets: [],
  defaultWidgets: [],
  error: false,
  success: false,
  maxWidgets: null
};

const widgetStateReducer = (state, { payload, type }) => {
  switch (type) {
    case UPDATE_MAX_WIDGETS: {
      const { maxWidgets } = payload;
      return {
        ...state,
        maxWidgets
      };
    }
    case UPDATE_WIDGET_LIST: {
      const { allWidgets, defaultWidgets, success } = payload;
      return {
        ...state,
        allWidgets,
        success,
        defaultWidgets
      };
    }
    default:
      return state;
  }
};

export const AddWidgetComponent = ({ workspaceId }) => {
  const { addWidgetContainer, helpText } = useStyles();
  const { REACT_APP_BUILD_NUMBER, isWeb } = useAppConfig();
  const locale = useLocale();
  const userPreferences = useContext(UserPreferencesContext);

  const [{ allWidgets, defaultWidgets, error, maxWidgets, success }, updateWidgetState] =
    useReducer(widgetStateReducer, initialState);

  const updateMaxWidgets = () => {
    subscriptions.getConfigSettings = getConfigSettings().subscribe(
      ({ data: { MAX_WORKSPACE_WIDGETS }, success }) => {
        if (success) {
          updateWidgetState({
            type: UPDATE_MAX_WIDGETS,
            payload: { maxWidgets: MAX_WORKSPACE_WIDGETS }
          });
        }
      }
    );
  };

  const setUserWidgetInformation = () => {
    return Promise.all([
      fetchAllWidgets(),
      fetchWorkspaceDefaults(workspaceId),
      userPreferences.get(workspaceId)
    ]).then(([{ success, data: allWidgets = [] }, defaultWidgets]) => {
      updateWidgetState({
        type: UPDATE_WIDGET_LIST,
        payload: {
          allWidgets,
          success,
          defaultWidgets
        }
      });
    });
  };

  useEffect(() => {
    updateMaxWidgets();
    setUserWidgetInformation();
    return () => unsubscribeComponent(subscriptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPreferences = () => {
    const prefs = userPreferences.getLocal(workspaceId);
    if (prefs) {
      return prefs;
    }

    return fetchWorkspaceDefaults(workspaceId).then((widgets) =>
      widgets
        .map((widget) => ({
          isDefault: true,
          locked: !widget.isRemovable && !widget.isRepositionable && !widget.isResizable,
          ...widget
        }))
        .filter(({ locked }) => !locked)
        .sort(({ rowNumber: rowA }, { rowNumber: rowB }) => rowA - rowB)
        .map((widget, index) => ({ ...widget, rowNumber: index }))
    );
  };

  const handleError = () => {
    /** @todo CB-19500 unsure where I should display this message */
    // console.error(e);
  };

  const addWidget = (value, buildNumber) => {
    const widget = allWidgets.find(({ widgetCode }) => widgetCode === value);
    if (!widget) {
      return Promise.resolve();
    }

    const addedWidget = {
      uniqueId: uniqId(`${widget.widgetCode}_`),
      ...widget
    };

    return Promise.resolve()
      .then(checkPreferences)
      .then((prefs) => {
        const unlockedIndex = prefs.findIndex(
          (pref) => pref.isRemovable || pref.isRemovable === undefined
        );
        if (unlockedIndex === -1) {
          userPreferences.set(workspaceId, [...prefs, addedWidget]);
          return;
        }
        const locked = prefs.slice(0, unlockedIndex);
        const unlocked = prefs.slice(unlockedIndex);

        userPreferences.set(workspaceId, [
          ...locked,
          { ...addedWidget, manuallyAdded: true, buildNumber, dateAdded: new Date() },
          ...unlocked
        ]);
      })
      .catch(handleError);
  };

  if (!success || !Array.isArray(allWidgets)) {
    return null;
  }

  const formedWidgetsLinks = allWidgets
    .map(({ widgetCode, widgetName }) => ({
      name: widgetName,
      value: widgetCode
    }))
    .sort((firstLink, secondLink) => (firstLink.name < secondLink.name ? -1 : 1));

  // add user preference widgets to locked default widgets
  const numWidgets =
    (userPreferences.getLocal(workspaceId)?.length || 0) +
    defaultWidgets.filter((widget) => isWidgetLocked(widget)).length;

  const disableAddWidget = maxWidgets && numWidgets >= maxWidgets;
  const showMaxWidgetLimit = maxWidgets && numWidgets + withinMaxWidgetLimit >= maxWidgets;

  const widgetLinks = [
    {
      name: locale['common.addWidget'],
      value: ''
    },
    ...formedWidgetsLinks
  ];

  return (
    <>
      {isWeb && (
        <div className={addWidgetContainer}>
          <div>
            <ListPill
              optionLinkRole="option"
              disabled={disableAddWidget}
              htmlId="addWidget"
              labelText={locale['common.addWidgetScreenreader']}
              links={widgetLinks}
              onChange={(value) => {
                addWidget(value, REACT_APP_BUILD_NUMBER);
              }}
            />
          </div>
          {showMaxWidgetLimit && (
            <span className={helpText} data-qa="add-widget-warning">
              {template(locale['common.manageWorkspace.help'])({
                max: maxWidgets,
                count: numWidgets
              })}
            </span>
          )}
        </div>
      )}
    </>
  );
};

AddWidgetComponent.propTypes = {
  workspaceId: PropTypes.string
};

AddWidgetComponent.defaultProps = {
  workspaceId: ''
};

export default AddWidgetComponent;
